import React, { useEffect, useState } from 'react'
import adminApi from './api'
import { ConfirmModal } from './components/ConfirmModal'
import { Notification } from './components/Notification'


interface User {
  id: number
  email: string
  name: string
  role: 'admin' | 'manager'
  is_active: boolean
  created_at: string
}

export const UsersAdmin: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [currentUserId, setCurrentUserId] = useState<number | null>(null)
  const [isOnlyAdmin, setIsOnlyAdmin] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'manager'
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{show: boolean, id: number | null}>({show: false, id: null})

  useEffect(() => {
    fetchUsers()
    fetchCurrentUser()
  }, [])

  const showNotification = (message: string, type: 'success' | 'error') => {
    setNotification({ message, type })
  }

  const fetchCurrentUser = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/me')
      setCurrentUserId(data.user.id)
    } catch (err) {
      console.error('Error fetching current user:', err)
    }
  }

  const fetchUsers = async () => {
    try {
      const { data } = await adminApi.get('/api/admin/users')
      setUsers(data.users)
    } catch (err) {
      console.error(err)
      showNotification('Ошибка загрузки пользователей', 'error')
    } finally {
      setLoading(false)
    }
  }

  const openModal = (user?: User) => {
    if (user) {
      setEditingUser(user)
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role
      })

      // Check if editing yourself and you're the only admin
      if (user.id === currentUserId && user.role === 'admin') {
        const adminCount = users.filter(u => u.role === 'admin' && u.is_active).length
        setIsOnlyAdmin(adminCount === 1)
      } else {
        setIsOnlyAdmin(false)
      }
    } else {
      setEditingUser(null)
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'manager'
      })
      setIsOnlyAdmin(false)
    }
    setShowModal(true)
    setFormErrors({})
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingUser(null)
    setFormErrors({})
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const errors: Record<string, string> = {}
    if (!formData.name.trim()) errors.name = 'Обязательное поле'
    if (!formData.email.trim()) errors.email = 'Обязательное поле'
    if (!editingUser && !formData.password.trim()) errors.password = 'Обязательное поле'
    if (!formData.role) errors.role = 'Обязательное поле'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    setSubmitting(true)
    try {
      if (editingUser) {
        const updateData: {
          name: string
          role: string
          password?: string
        } = {
          name: formData.name,
          role: formData.role
        }
        if (formData.password.trim()) {
          updateData.password = formData.password
        }
        await adminApi.put(`/api/admin/users/${editingUser.id}`, updateData)
        showNotification('Пользователь успешно обновлен!', 'success')
      } else {
        await adminApi.post('/api/admin/users', formData)
        showNotification('Пользователь успешно создан!', 'success')
      }

      closeModal()
      fetchUsers()
    } catch (err: unknown) {
      console.error('Error saving user:', err)
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка при сохранении пользователя')
        : 'Ошибка при сохранении пользователя'
      showNotification(errorMessage, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = (id: number) => {
    setConfirmDelete({show: true, id})
    setOpenMenuId(null)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete.id) return

    try {
      await adminApi.delete(`/api/admin/users/${confirmDelete.id}`)
      showNotification('Пользователь успешно удален!', 'success')
      fetchUsers()
    } catch (err: unknown) {
      console.error('Error deleting user:', err)
      const errorMessage = err instanceof Error && 'response' in err
        ? ((err as any).response?.data?.message || 'Ошибка при удалении пользователя')
        : 'Ошибка при удалении пользователя'
      showNotification(errorMessage, 'error')
    } finally {
      setConfirmDelete({show: false, id: null})
    }
  }

  const getRoleLabel = (role: string) => {
    if (role === 'admin') return 'Админ'
    if (role === 'manager') return 'Менеджер'
    return role
  }

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header flex items-center justify-between gap-4">
        <div>
          <h1 className="page-title">Пользователи</h1>
          <p className="page-subtitle">Управление администраторами системы</p>
        </div>

        <button
          onClick={() => openModal()}
          className="btn-primary"
        >
          Добавить пользователя
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      ) : (
        <div className="table-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Email</th>
                <th>Роль</th>
                <th>Активен</th>
                <th>Создан</th>
                <th className="w-[60px]"></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <div className="empty-state-text">Пользователи не найдены</div>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      {getRoleLabel(user.role)}
                    </td>
                    <td>
                      {user.is_active ? 'Да' : 'Нет'}
                    </td>
                    <td className="text-[13px]">
                      {new Date(user.created_at).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                        className="bg-transparent border-0 cursor-pointer px-2 py-1 text-xl text-gray-500 leading-none"
                      >
                        ⋮
                      </button>
                      {openMenuId === user.id && (
                        <>
                          <div
                            className="fixed inset-0 z-[999]"
                            onClick={() => setOpenMenuId(null)}
                          />
                          <div className="absolute right-0 top-full bg-white border border-[#e0e0e0] rounded shadow-md z-[1000] min-w-[150px] mt-1">
                            <button
                              onClick={() => {
                                setOpenMenuId(null)
                                openModal(user)
                              }}
                              className="block w-full px-4 py-2.5 border-0 bg-transparent text-left cursor-pointer text-sm text-[#333] hover:bg-[#f5f5f5]"
                            >
                              Изменить
                            </button>
                            {user.id !== currentUserId && (
                              <button
                                onClick={() => handleDelete(user.id)}
                                className="block w-full px-4 py-2.5 border-0 bg-transparent text-left cursor-pointer text-sm text-[#ff4d4f] hover:bg-[#fff2f0]"
                              >
                                Удалить
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingUser ? 'Редактировать пользователя' : 'Добавить пользователя'}
              </h2>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="modal-field">
                  <label className="modal-label">
                    Имя <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={formErrors.name ? 'form-control border-red-500' : 'form-control'}
                  />
                  {formErrors.name && (
                    <div className="field-error">
                      {formErrors.name}
                    </div>
                  )}
                </div>

                <div className="modal-field">
                  <label className="modal-label">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-control ${formErrors.email ? 'border-red-500' : ''} ${
                      editingUser ? 'bg-[#f5f5f5] cursor-not-allowed' : ''
                    }`}
                    disabled={!!editingUser}
                  />
                  {formErrors.email && (
                    <div className="field-error">
                      {formErrors.email}
                    </div>
                  )}
                </div>

                <div className="modal-field">
                  <label className="modal-label">
                    Пароль {!editingUser && <span className="text-red-500">*</span>}
                    {editingUser && <span className="text-xs text-gray-500 font-normal"> (оставьте пустым, чтобы не изменять)</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={formErrors.password ? 'form-control border-red-500' : 'form-control'}
                    placeholder={editingUser ? 'Введите новый пароль' : ''}
                  />
                  {formErrors.password && (
                    <div className="field-error">
                      {formErrors.password}
                    </div>
                  )}
                </div>

                <div className="modal-field">
                  <label className="modal-label">
                    Роль <span className="text-red-500">*</span>
                    {isOnlyAdmin && <span className="text-xs text-gray-500 font-normal"> (вы единственный админ)</span>}
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className={`form-control ${formErrors.role ? 'border-red-500' : ''} ${
                      isOnlyAdmin ? 'bg-[#f5f5f5] cursor-not-allowed' : ''
                    }`}
                    disabled={isOnlyAdmin}
                  >
                    <option value="manager">Менеджер</option>
                    <option value="admin">Админ</option>
                  </select>
                  {formErrors.role && (
                    <div className="field-error">
                      {formErrors.role}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={`btn-primary ${submitting ? 'opacity-60' : ''}`}
                  disabled={submitting}
                >
                  {submitting ? (editingUser ? 'Сохранение...' : 'Создание...') : (editingUser ? 'Сохранить' : 'Создать пользователя')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.show}
        title="Подтвердите удаление"
        message="Вы уверены, что хотите удалить этого пользователя? Это действие нельзя отменить."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete({show: false, id: null})}
        confirmText="Удалить"
        cancelText="Отмена"
        isDanger={true}
      />
    </div>
  )
}
